import { useQuery } from "@apollo/client";
import { GET_INVITE_WITH_SESSIONS } from "../gql";
import type {
  Conversation,
  Connection,
  ReadConnectionByCodeResult,
  ConversationsConnection,
} from "@casey/db-types";

interface InviteWithSessionsResponse {
  readConnectionByCode: ReadConnectionByCodeResult;
  conversations: ConversationsConnection;
}

interface InviteWithSessionsVariables {
  connectionCode: string;
  participantUserId: string;
}

/**
 * Combined hook to fetch invite details and existing sessions in a single query
 * This prevents race conditions and flickering when checking for existing sessions
 */
export function useInviteWithSessions(
  connectionCode: string,
  participantUserId: string,
  options: { skip?: boolean } = {},
) {
  const { data, loading, error, refetch } = useQuery<
    InviteWithSessionsResponse,
    InviteWithSessionsVariables
  >(GET_INVITE_WITH_SESSIONS, {
    variables: {
      connectionCode,
      participantUserId,
    },
    skip: !connectionCode || !participantUserId || options.skip,
    errorPolicy: "all",
  });

  const invite = data?.readConnectionByCode?.connection;
  const sessions = data?.conversations?.edges?.map((edge) => edge.node) || [];

  // Helper function to check if a session is resumable
  const isSessionResumable = (session: Conversation): boolean => {
    if (!session.conversation) {
      return false;
    }

    let messages: any[] = [];
    try {
      if (typeof session.conversation === "string") {
        messages = JSON.parse(session.conversation);
      } else if (Array.isArray(session.conversation)) {
        messages = session.conversation;
      } else {
        return false;
      }
    } catch (e) {
      console.error("Failed to parse conversation:", e);
      return false;
    }

    if (!Array.isArray(messages) || messages.length === 0) {
      return false;
    }

    const agentMessages = messages.filter(
      (msg: any) => msg.role === "assistant",
    );
    const userMessages = messages.filter((msg: any) => msg.role === "user");

    if (agentMessages.length === 0) {
      return false;
    }

    // If only greeting exchanged, not worth resuming
    if (agentMessages.length === 1 && userMessages.length <= 1) {
      return false;
    }

    return true;
  };

  // Process sessions
  const latestSession = sessions.length > 0 ? sessions[0] : null;
  const latestIncompleteSession = sessions.find(
    (session) =>
      session && session.status !== "completed" && isSessionResumable(session),
  );

  // ReadConnectionByCodeResult already has greetingTitle and greetingBody processed
  const greetingTitle = data?.readConnectionByCode?.greetingTitle || "";
  const greetingBody = data?.readConnectionByCode?.greetingBody || "";
  const organizationSlug = data?.readConnectionByCode?.organizationSlug || "";
  const organizationName = data?.readConnectionByCode?.organizationName || "";
  const organizationAvatarUrl =
    data?.readConnectionByCode?.organizationAvatarUrl || "";

  return {
    invite,
    sessions,
    latestSession,
    latestIncompleteSession,
    greetingTitle,
    greetingBody,
    organizationSlug,
    organizationName,
    organizationAvatarUrl,
    loading,
    error,
    refetch,
  };
}
